// src/errors.ts
var LootApiError = class extends Error {
  code;
  status;
  details;
  constructor(code, message, status, details) {
    super(message);
    this.name = "LootApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
};
async function throwFromResponse(res) {
  let code = "internal";
  let message = `request failed with status ${res.status}`;
  let details;
  try {
    const body = await res.json();
    if (body.error?.code) code = body.error.code;
    if (body.error?.message) message = body.error.message;
    details = body.error?.details;
  } catch {
  }
  throw new LootApiError(code, message, res.status, details);
}

// src/liveBreaks.ts
function queryString(params) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== void 0) query.set(key, String(value));
  }
  return query.size ? `?${query}` : "";
}
var id = encodeURIComponent;
var LiveBreaksApi = class {
  constructor(request) {
    this.request = request;
  }
  request;
  // ---- watching ----------------------------------------------------------------------------
  /** The directory: public live breaks open or closed under five minutes ago, newest first, 24 a page. */
  async list(params = {}) {
    return this.request("GET", `/v1/live-breaks${queryString({ ...params })}`, {
      auth: "optional-session"
    });
  }
  /** Open public live breaks streaming on any of these Twitch logins (at most 100). */
  async listByChannel(logins) {
    return this.request(
      "GET",
      `/v1/live-breaks/by-channel${queryString({ logins: logins.join(",") })}`,
      {
        auth: "optional-session"
      }
    );
  }
  /**
   * The Twitch streamers staff show in the widget now, priority first: the sponsored row while one
   * hosts a live break (`listByChannel` with their logins), the promoted stream while one is live on
   * Twitch without one. None when the store has no Twitch app.
   */
  async listSponsoredStreams() {
    return this.request("GET", "/v1/live-breaks/sponsored-streams", { auth: "optional-session" });
  }
  /** One live break: its boxes at live-break prices, the entry on stage, and your own fields. */
  async get(liveBreakId) {
    return this.request("GET", `/v1/live-breaks/${id(liveBreakId)}`, { auth: "optional-session" });
  }
  /** The queue in order, the entry presenting included; merge pages and events by `queueKey`. */
  async getQueue(liveBreakId, params = {}) {
    return this.request("GET", `/v1/live-breaks/${id(liveBreakId)}/queue${queryString(params)}`, {
      auth: "optional-session"
    });
  }
  /** The wait for `entryId`, or for a purchase made now (with a session, its priority too). */
  async getEstimate(liveBreakId, params = {}) {
    return this.request(
      "GET",
      `/v1/live-breaks/${id(liveBreakId)}/estimate${queryString(params)}`,
      {
        auth: "optional-session"
      }
    );
  }
  /**
   * Boxes priced as a live break would sell them: at `rateBps`, else `liveBreakId`'s rate, else your
   * host rate, else the site's default. A search lists only boxes a purchase would accept; `ids`
   * reads those boxes, eligible or not.
   */
  async searchBoxes(params = {}) {
    const { ids, ...rest } = params;
    return this.request(
      "GET",
      `/v1/live-breaks/boxes${queryString({ ...rest, ids: ids?.join(",") })}`,
      { auth: "optional-session" }
    );
  }
  /** The playlists a host copies a new live break's boxes from, priced like `searchBoxes`. */
  async listPlaylists(params = {}) {
    return this.request("GET", `/v1/live-breaks/playlists${queryString(params)}`, {
      auth: "optional-session"
    });
  }
  // ---- buying --------------------------------------------------------------------------------
  /**
   * Buys 1–5 of one box into the queue at `expectedPriceCents`, the live-break price you showed
   * (`live_break_price_changed` otherwise). Nothing is debited: the price is held, and the Reveal
   * later writes a reveal you claim or sell back. Retries are safe: an Idempotency-Key is generated
   * per logical purchase.
   */
  async purchase(liveBreakId, args) {
    const { idempotencyKey, ...body } = args;
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/entries`, {
      auth: "session",
      headers: { "idempotency-key": idempotencyKey ?? crypto.randomUUID() },
      body
    });
  }
  /** Leaves the queue: your entry is released and its hold given back. */
  async cancelEntry(liveBreakId, entryId) {
    return this.request(
      "POST",
      `/v1/live-breaks/${id(liveBreakId)}/entries/${id(entryId)}/cancel`,
      { auth: "session" }
    );
  }
  /** Your entries in one live break: `opened` (default, the results) or `queued`. */
  async listMyEntries(liveBreakId, params = {}) {
    return this.request(
      "GET",
      `/v1/live-breaks/${id(liveBreakId)}/me/entries${queryString(params)}`,
      { auth: "session" }
    );
  }
  /** Every entry you still have queued, in every live break, with its place and wait. */
  async listMyQueuedEntries() {
    return this.request("GET", "/v1/me/live-breaks/entries", { auth: "session" });
  }
  /**
   * The live breaks you are hosting now, Draining ones included, newest first: `getHost`'s
   * `openLiveBreaks` alone, light enough to poll.
   */
  async listHosting() {
    return this.request("GET", "/v1/me/live-breaks/hosting", { auth: "session" });
  }
  /** Claim all: purchases every reveal you hold from this live break, all or none. */
  async claimReveals(liveBreakId) {
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/reveals/claim`, {
      auth: "session"
    });
  }
  // ---- hosting -------------------------------------------------------------------------------
  /** Opens a live break you host. */
  async create(args) {
    return this.request("POST", "/v1/live-breaks", { auth: "session", body: args });
  }
  /** Edits your open live break; an omitted key leaves the setting alone. */
  async update(liveBreakId, patch) {
    return this.request("PATCH", `/v1/live-breaks/${id(liveBreakId)}`, {
      auth: "session",
      body: patch
    });
  }
  /** Next, or Open now from `presenting`, against the phase (and entry) your stage showed. */
  async advance(liveBreakId, args) {
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/advance`, {
      auth: "session",
      body: args
    });
  }
  /** Picks a queued entry into the front lane, behind Up Next. */
  async pickEntry(liveBreakId, entryId) {
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/entries/${id(entryId)}/pick`, {
      auth: "session"
    });
  }
  /** End: purchases stop and the queue plays out, then the live break closes. Idempotent. */
  /** The box versions this live break's entries pinned, with their drop tables (an entry's `boxVersionId`). */
  async getBoxVersions(liveBreakId, boxVersionIds) {
    return this.request(
      "GET",
      `/v1/live-breaks/${id(liveBreakId)}/box-versions${queryString({ ids: boxVersionIds.join(",") })}`,
      { auth: "optional-session" }
    );
  }
  /** Marks you watching (every 45 s while the page is open); the host's heartbeat stamps them present. */
  async heartbeat(liveBreakId) {
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/heartbeat`, {
      auth: "session"
    });
  }
  /** Who is watching: the count and the newest viewers as public profiles. */
  async getViewers(liveBreakId) {
    return this.request("GET", `/v1/live-breaks/${id(liveBreakId)}/viewers`, {
      auth: "optional-session"
    });
  }
  /** The host announces a public live break in the site chat, once. */
  async announce(liveBreakId) {
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/announce`, { auth: "session" });
  }
  async end(liveBreakId) {
    return this.request("POST", `/v1/live-breaks/${id(liveBreakId)}/end`, { auth: "session" });
  }
  /** You as a host: rate, commission per pool, all-time figures and your open live breaks. */
  async getHost() {
    return this.request("GET", "/v1/me/live-break-host", { auth: "session" });
  }
  /** Your lifetime per-box spend and opens across every live break you hosted. */
  async getHostBoxStats() {
    return this.request("GET", "/v1/me/live-break-host/box-stats", { auth: "session" });
  }
  /** Sets your own host rate (basis points) for live breaks you open from now on; null = the default. */
  async setHostRate(rateBps) {
    return this.request("PUT", "/v1/me/live-break-host/rate", {
      auth: "session",
      body: { rateBps }
    });
  }
  /** Claims your host commission: each pool's whole cents, as one `live_break_commission` ledger entry. */
  async claimCommission(args = {}) {
    return this.request("POST", "/v1/me/live-break-host/commission/claim", {
      auth: "session",
      headers: { "idempotency-key": args.idempotencyKey ?? crypto.randomUUID() }
    });
  }
  /** The live breaks you hosted, newest first, 20 a page, each with its totals and top pull. */
  async listHostedLiveBreaks(params = {}) {
    return this.request("GET", `/v1/me/live-break-host/live-breaks${queryString(params)}`, {
      auth: "session"
    });
  }
  /** One live break you hosted, with every stat and its top pulls. */
  async getHostedLiveBreak(liveBreakId) {
    return this.request("GET", `/v1/me/live-break-host/live-breaks/${id(liveBreakId)}`, {
      auth: "session"
    });
  }
  /** The shoppers you blocked, newest first. */
  async listBlocks() {
    return this.request("GET", "/v1/me/live-break-host/blocks", { auth: "session" });
  }
  /** Blocks a shopper from every live break of yours, releasing their queued entries. Idempotent. */
  async block(endUserId) {
    return this.request("POST", "/v1/me/live-break-host/blocks", {
      auth: "session",
      body: { endUserId }
    });
  }
  /** Lifts a Block of yours. */
  async unblock(endUserId) {
    return this.request("DELETE", `/v1/me/live-break-host/blocks/${id(endUserId)}`, {
      auth: "session"
    });
  }
};

// src/twitch.ts
var TwitchApi = class {
  constructor(request) {
    this.request = request;
  }
  request;
  /** Up to 100 channels by login, at most a minute old: found, live, viewers, name and avatar. */
  async getChannels(logins) {
    const query = new URLSearchParams({ logins: logins.join(",") });
    return this.request("GET", `/v1/twitch/channels?${query}`, { auth: "optional-session" });
  }
  /** Whether the store has Twitch, and your link (validated when due, so it may refresh). */
  async getMe() {
    return this.request("GET", "/v1/me/twitch", { auth: "session" });
  }
  /** Links your Twitch account from the code Twitch's redirect carried, for the same redirect URI. */
  async link(input) {
    return this.request("POST", "/v1/me/twitch/link", { auth: "session", body: input });
  }
  /** Unlinks your Twitch account; `twitch_not_linked` when none is. */
  async unlink() {
    return this.request("DELETE", "/v1/me/twitch", { auth: "session" });
  }
};

// src/currency.ts
var DEFAULT_CURRENCY = {
  name: "credit",
  namePlural: "credits",
  symbol: "$",
  multiplier: 100,
  iconSvg: ""
};
var USD = {
  name: "dollar",
  namePlural: "dollars",
  symbol: "$",
  multiplier: 100,
  iconSvg: ""
};
function currencyFractionDigits(multiplier) {
  switch (multiplier) {
    case 1:
      return 0;
    case 10:
      return 1;
    case 100:
      return 2;
    case 1e3:
      return 3;
  }
}
var NUMBER_FORMATS = [0, 1, 2, 3].map(
  (digits) => new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })
);
function formatAmount(cents, currency, opts = {}) {
  if (!Number.isFinite(cents)) {
    throw new RangeError(`formatAmount: amount is not a finite number of cents: ${cents}`);
  }
  const digits = currencyFractionDigits(currency.multiplier);
  const value = Math.abs(cents) / currency.multiplier;
  const sign = cents < 0 ? "-" : "";
  const body = opts.compact && value >= 1e3 ? `${(value / 1e3).toFixed(1)}k` : NUMBER_FORMATS[digits].format(value);
  return `${sign}${currency.symbol}${body}`;
}
function currencyUnitName(cents, currency) {
  return cents === currency.multiplier ? currency.name : currency.namePlural;
}
function formatAmountWithUnit(cents, currency) {
  const amount = formatAmount(cents, currency);
  return currency.symbol === "" ? `${amount} ${currencyUnitName(cents, currency)}` : amount;
}

// src/index.ts
var DEFAULT_BASE_URL = "https://api.lootos.dev";
var LootClient = class {
  config;
  sessionToken;
  fetchImpl;
  baseUrl;
  /** Live breaks: the directory, a live break's page and queue, buying, and hosting. */
  liveBreaks;
  /** Twitch through the store's own app: channel status and the shopper's link. */
  twitch;
  constructor(config) {
    this.config = config;
    this.sessionToken = config.sessionToken ?? null;
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
    this.baseUrl = (config.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, "");
    this.liveBreaks = new LiveBreaksApi(
      (method, path, opts) => this.request(method, path, {
        session: opts.auth === "session" ? true : "optional",
        headers: opts.headers,
        body: opts.body
      })
    );
    this.twitch = new TwitchApi(
      (method, path, opts) => this.request(method, path, {
        session: opts.auth === "session" ? true : "optional",
        headers: opts.headers,
        body: opts.body
      })
    );
  }
  setSessionToken(token) {
    this.sessionToken = token;
  }
  getSessionToken() {
    return this.sessionToken;
  }
  // ---- boxes (publishable key) --------------------------------------------
  /**
   * The boxes your site offers. Forge boxes (shopper-built, `COMMUNITY`) are left out unless
   * `type: "COMMUNITY"` asks for them alone.
   */
  async listBoxes(opts = {}) {
    const query = opts.type ? `?type=${encodeURIComponent(opts.type)}` : "";
    const body = await this.request("GET", `/v1/boxes${query}`);
    return body.data;
  }
  async getBox(boxId) {
    const body = await this.request(
      "GET",
      `/v1/boxes/${encodeURIComponent(boxId)}`
    );
    return body.box;
  }
  // ---- end-user session surface ---------------------------------------------
  async getMe() {
    const body = await this.request("GET", "/v1/me", { session: true });
    return body.user;
  }
  /** The user's balance in cents plus the environment's display currency (see `formatAmount`). */
  async getBalance() {
    return this.request("GET", "/v1/me/balance", { session: true });
  }
  async listMyOpens(params) {
    const query = new URLSearchParams();
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.size ? `?${query}` : "";
    return this.request("GET", `/v1/me/opens${qs}`, { session: true });
  }
  /**
   * Opens a box: reveals items without debiting the balance. Purchase them with
   * claimReveal (or turn on auto-claim via updateMySettings). Requires a
   * session token. Retries are safe: an Idempotency-Key is generated per
   * logical open.
   */
  async openBox(args) {
    const idempotencyKey = args.idempotencyKey ?? crypto.randomUUID();
    return this.request("POST", `/v1/boxes/${encodeURIComponent(args.boxId)}/opens`, {
      session: true,
      headers: { "idempotency-key": idempotencyKey },
      body: {
        clientSeed: args.clientSeed,
        amount: args.amount,
        expectedVersion: args.expectedVersion
      }
    });
  }
  /**
   * Purchases the revealed items of an open group at their disclosed price.
   * Idempotent per reveal: a repeat claim replays the original result.
   */
  async claimReveal(args) {
    return this.request(
      "POST",
      `/v1/reveals/${encodeURIComponent(args.openGroupId)}/claim`,
      {
        session: true,
        body: { expectedPurchaseAmount: args.expectedPurchaseAmount }
      }
    );
  }
  /**
   * Purchases the revealed items at their disclosed price and, in the same transaction, sells
   * every one back to the house for its value: the balance moves by Σ value − price and the
   * ledger shows a `claim_debit` then a `sell_credit`. Idempotent per reveal; a reveal already
   * claimed as owned items answers `reveal_already_claimed`.
   */
  async sellBackReveal(args) {
    return this.request(
      "POST",
      `/v1/reveals/${encodeURIComponent(args.openGroupId)}/sell-back`,
      {
        session: true,
        body: { expectedPurchaseAmount: args.expectedPurchaseAmount }
      }
    );
  }
  /**
   * The user's inventory, newest first: the units they own after a claim. `status` narrows to
   * one of them — `owned` is what can be sold back or withdrawn.
   */
  async listMyInventory(params) {
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.size ? `?${query}` : "";
    return this.request("GET", `/v1/me/inventory${qs}`, {
      session: true
    });
  }
  /**
   * Sells owned units back to the house for Σ their value, crediting the balance in one
   * transaction. All or nothing: a unit that cannot be sold — already sold, being shipped, or
   * worth nothing — refuses the sale with `inventory_item_not_sellable` and names it in
   * `details.inventoryItemId` and `details.reason`.
   */
  async sellInventoryItems(args) {
    const body = await this.request(
      "POST",
      "/v1/me/inventory/sell",
      { session: true, body: { inventoryItemIds: args.inventoryItemIds } }
    );
    return body.sale;
  }
  /**
   * Sells one owned unit back to the house for its value: the sale above with a single id, for
   * an inventory tile that sells the unit it sits on. Refused the same way, with
   * `inventory_item_not_sellable` naming the unit and the reason.
   */
  async sellInventoryItem(args) {
    const body = await this.request(
      "POST",
      `/v1/me/inventory/${encodeURIComponent(args.inventoryItemId)}/sell`,
      { session: true }
    );
    return body.sale;
  }
  /**
   * Redeems a gift code into the user's balance as one `gift_code` ledger entry; dashes, spaces
   * and case in `code` are ignored. Rejects with `gift_code_not_redeemable`, whose
   * `details.reason` says why (`unknown`, `redeemed`, `expired` or `voided`), and with
   * `account_locked` on a locked account.
   */
  async redeemGiftCode(code) {
    return this.request("POST", "/v1/me/gift-codes/redeem", {
      session: true,
      body: { code }
    });
  }
  /**
   * The user's notifications, newest first, with the unread count beside them. `unread: true`
   * lists only unread ones; pass `nextCursor` back as `cursor` for the next page.
   */
  async listNotifications(params) {
    const query = new URLSearchParams();
    if (params?.cursor) query.set("cursor", params.cursor);
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.unread) query.set("unread", "true");
    const qs = query.size ? `?${query}` : "";
    return this.request("GET", `/v1/me/notifications${qs}`, {
      session: true
    });
  }
  /** The unread count alone: a bell's badge. */
  async getUnreadNotificationCount() {
    const body = await this.request(
      "GET",
      "/v1/me/notifications/unread-count",
      { session: true }
    );
    return body.unreadCount;
  }
  /** Marks the named notifications, or every unread one (`{ all: true }`), read. */
  async markNotificationsRead(args) {
    return this.request("POST", "/v1/me/notifications/read", {
      session: true,
      body: args
    });
  }
  /** The categories the environment sends, each with whether the user receives it. */
  async getNotificationSettings() {
    return this.request("GET", "/v1/me/notification-settings", {
      session: true
    });
  }
  /**
   * Replaces the user's opt-outs, whole: a category left out is turned back on. Naming a required
   * category (`orders`, `balance`, `account`) rejects with `invalid_request`.
   */
  async updateNotificationSettings(args) {
    return this.request("PUT", "/v1/me/notification-settings", {
      session: true,
      body: args
    });
  }
  /** The user's outstanding unclaimed reveal, if any (at most one exists). */
  async getUnclaimedReveal() {
    const body = await this.request(
      "GET",
      "/v1/me/reveals/unclaimed",
      { session: true }
    );
    return body.reveal;
  }
  async updateMySettings(args) {
    const body = await this.request("PUT", "/v1/me/settings", {
      session: true,
      body: args
    });
    return body.user;
  }
  /**
   * Sets or changes the user's public username: 3–20 letters, digits or underscores, unique per
   * environment regardless of case. Rejects with `username_taken` when another user holds it and
   * `invalid_request` when the name breaks a rule (the message names it).
   */
  async updateMyUsername(username) {
    const body = await this.request("PUT", "/v1/me/username", {
      session: true,
      body: { username }
    });
    return body.user;
  }
  /**
   * Starts (or resumes) identity verification for the current user where the site requires it.
   * Open the returned `url` in a new tab; the outcome arrives later on `getMe().kycStatus` /
   * `kycVerified`. Rejects with `invalid_request` when verification is not required here or a
   * decision already stands.
   */
  async startKycSession(args) {
    return this.request("POST", "/v1/me/kyc/session", {
      session: true,
      body: args
    });
  }
  // ---- profiles (any key or session) ---------------------------------------------
  /**
   * An end user as other users may see them: display name, member-since and counts. `endUserId`
   * is what a battle seat's `endUser.id` carries. Rejects with `not_found` for an id outside this
   * key's environment.
   */
  async getProfile(endUserId) {
    const body = await this.request(
      "GET",
      `/v1/profiles/${encodeURIComponent(endUserId)}`
    );
    return body.profile;
  }
  // ---- fairness (public) --------------------------------------------------------
  async getSeed(date) {
    return this.request("GET", `/v1/fairness/seeds/${encodeURIComponent(date)}`);
  }
  async getOpenVerification(openId) {
    return this.request(
      "GET",
      `/v1/opens/${encodeURIComponent(openId)}/verification`
    );
  }
  // ---- internals -------------------------------------------------------------------
  async request(method, path, opts = {}) {
    const headers = {
      "x-publishable-key": this.config.publishableKey,
      ...opts.headers
    };
    if (opts.session === true && !this.sessionToken) {
      throw new LootApiError("unauthorized", "no session token set", 401);
    }
    const sendsSession = opts.session !== void 0 && opts.session !== false && this.sessionToken;
    if (sendsSession) headers.authorization = `Bearer ${this.sessionToken}`;
    if (opts.body !== void 0) headers["content-type"] = "application/json";
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: opts.body !== void 0 ? JSON.stringify(opts.body) : void 0
    });
    if (res.ok) return await res.json();
    if (res.status === 401 && sendsSession && !opts.isRetry && this.config.onSessionExpired) {
      const fresh = await this.config.onSessionExpired();
      if (fresh) {
        this.sessionToken = fresh;
        return this.request(method, path, { ...opts, isRetry: true });
      }
    }
    return throwFromResponse(res);
  }
};

export {
  LootApiError,
  throwFromResponse,
  LiveBreaksApi,
  TwitchApi,
  DEFAULT_CURRENCY,
  USD,
  currencyFractionDigits,
  formatAmount,
  currencyUnitName,
  formatAmountWithUnit,
  DEFAULT_BASE_URL,
  LootClient
};
//# sourceMappingURL=chunk-EJUE2OOO.js.map